import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import {
  reportingPeriodsEqual,
  validateReportingPeriodV01,
  type ReportingPeriodV01,
} from '../../../hooks/advisory/advisoryReportingPeriod';
import { focusRing, pillAction, pressable } from '../../../styles/visualLanguage';
import { stepPosition } from '../advisoryStepPosition';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { AdvisoryPeriodEditor } from './AdvisoryPeriodEditor';

export { ADVISORY_PERIOD_RANGE_OPTIONS } from './AdvisoryPeriodEditor';

export interface AdvisoryPeriodConfirmationScreenProps {
  period: ReportingPeriodV01;
  defaulted: boolean;
  extractionFailed: boolean;
  acceptStoredProposal?: boolean;
  onConfirm: (period?: ReportingPeriodV01) => void | Promise<unknown>;
}

export function AdvisoryPeriodConfirmationScreen({
  period,
  defaulted,
  extractionFailed,
  acceptStoredProposal = true,
  onConfirm,
}: AdvisoryPeriodConfirmationScreenProps) {
  const { t } = useUntypedTranslation();
  const [draft, setDraft] = useState(period);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    setDraft(period);
  }, [period]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const normalized = validateReportingPeriodV01(draft);
  const handleConfirm = async () => {
    if (!normalized.ok || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm(
        acceptStoredProposal && reportingPeriodsEqual(normalized.value, period)
          ? undefined
          : normalized.value
      );
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  };
  const position = stepPosition('goal', null);
  const hintKey = extractionFailed
    ? 'advisory:period.extractionFailedHint'
    : defaulted
    ? 'advisory:period.defaultedHint'
    : 'advisory:period.hint';
  return (
    <AdvisoryEngagementShell
      position={position?.position ?? '02'}
      total={position?.total ?? null}
      eyebrowKey="advisory:period.eyebrow"
      titleKey="advisory:period.title"
      hintKey={hintKey}
      actions={
        <Button
          variant="contained"
          disableRipple
          disabled={!normalized.ok || submitting}
          data-testid="advisory-period-confirm"
          onClick={() => void handleConfirm()}
          sx={[pillAction, pressable, focusRing]}
        >
          {t(submitting ? 'advisory:period.confirmingAction' : 'advisory:period.confirmAction')}
        </Button>
      }
    >
      <AdvisoryPeriodEditor
        value={draft}
        onChange={value => {
          if (!submittingRef.current) setDraft(value);
        }}
        busy={submitting}
        autoFocusYear={extractionFailed}
      />
    </AdvisoryEngagementShell>
  );
}

export default AdvisoryPeriodConfirmationScreen;
